import { useState, useCallback } from "react";
import { Alert, Button, Divider, Grid, Switch, Typography, FormControlLabel } from "@mui/material";

import { ServerValidationErrors } from "components/ServerValidationErrors";
import { ProductSelector } from "domains/products/components";
import { AddMultipleProducts } from "features/allocations";
import { ManagedProductStatus } from "domains/allocations/components/managed-product-status";
import { updateManagedProducts } from "domains/allocations/services/allocation-service";
import { SelectedProductDetails } from "domains/products/components/product-selector/ProductSelector";
import localisations from "domains/allocations/localisation";

interface IProps {
    fieldCompanyId: number;
}

const AddManagedProduct = (props: IProps) => {
    const { fieldCompanyId } = props;
    const { markProductAsManagedButtonText } = localisations.addManagedProduct;

    const [selectedProductDetails, setSelectedProductDetails] = useState(null as SelectedProductDetails | null);
    const [managedProductStatus, setManagedProductStatus] = useState(null as boolean | null);
    const [refresh, setRefresh] = useState(false);
    const [apiError, setApiError] = useState(null as Error | null);
    const [isMultipleProducts, setIsMultipleProducts] = useState(false);

    const onProductSelected = useCallback((productDetails: SelectedProductDetails | null): void => {
        setManagedProductStatus(null);
        setApiError(null);
        setSelectedProductDetails(productDetails);
    }, []);

    const onStatusLoaded = useCallback((status: boolean): void => {
        setRefresh(false);
        setManagedProductStatus(status);
    }, []);

    const markProductAsManaged = useCallback(async (productId: number): Promise<void> => {
        setApiError(null);
        setManagedProductStatus(null);

        try {
            await updateManagedProducts({ productIds: [productId], ledgerManaged: true });
        } catch (ex: any) {
            setApiError(ex as Error);
        }

        setRefresh(true);
    }, []);

    const toggleIsMultiple = () => {
        setIsMultipleProducts(!isMultipleProducts);
        setSelectedProductDetails(null);
        setManagedProductStatus(null);
        setRefresh(false);
    }

    const isSaveButtonEnabled = (): boolean => managedProductStatus === false && selectedProductDetails !== null;

    const getErrors = (error: Error) => {
        const validationErrors = ServerValidationErrors({
            error: error,
            localisations: localisations.addManagedProduct.serverErrors,
            title: localisations.addManagedProduct.errorTitle,
        });

        return !validationErrors ? (
            <Alert severity="error">
                {localisations.addManagedProduct.errorTitle}:<br />
                {error.message}
            </Alert>
        ) : (
            validationErrors
        );
    };

    return (
        <>
            <Typography sx={{ pb: 2 }}>{localisations.addManagedProduct.warningText}</Typography>
            <FormControlLabel sx={{ mb: 2 }}
                control={ <Switch onChange={toggleIsMultiple} />}
                label={localisations.addMultipleManagedProducts.switchLabel}
                labelPlacement="end"
            />



            {!isMultipleProducts ? (
                <Grid container>
                    <Grid item xs={12}>
                        <ProductSelector fieldCompanyId={fieldCompanyId} onSelected={onProductSelected} />
                        <ManagedProductStatus
                            productId={selectedProductDetails?.id ?? null}
                            onLoaded={onStatusLoaded}
                            forceRefresh={refresh}
                        />
                        <Divider sx={{ mb: 2, mt: 2 }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            aria-label={markProductAsManagedButtonText}
                            onClick={() => selectedProductDetails?.id && markProductAsManaged(selectedProductDetails?.id)}
                            disabled={!isSaveButtonEnabled()}
                            variant="contained"
                        >
                            {markProductAsManagedButtonText}
                        </Button>
                    </Grid>

                    {!apiError ? (
                        <></>
                    ) : (
                        <Grid item xs={12}>
                            {getErrors(apiError as Error)}
                        </Grid>
                    )}
                </Grid>
            ) : (
                <Grid container>
                    <Grid item xs={12}>
                        <AddMultipleProducts fieldCompanyId={fieldCompanyId} />
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default AddManagedProduct;
